/**
 * Crop utility using canvas. Given an image URL and pixel crop area,
 * returns a data URL of the cropped image at a target output size (for high DPI).
 * Honours the rotation applied in the cropper.
 */
export async function getCroppedDataUrl(
  imageUrl,
  pixelCrop,
  outputWidth,
  outputHeight,
  rotation = 0,
) {
  const image = await loadImage(imageUrl);
  const source = rotation ? rotateToCanvas(image, rotation) : image;

  const canvas = document.createElement("canvas");
  canvas.width = outputWidth;
  canvas.height = outputHeight;
  const ctx = canvas.getContext("2d");
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(
    source,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    outputWidth,
    outputHeight,
  );
  return canvas.toDataURL("image/png");
}

/** Returns a data URL of the image mirrored horizontally. */
export async function flipImageHorizontally(imageUrl) {
  const image = await loadImage(imageUrl);
  const canvas = document.createElement("canvas");
  canvas.width = image.width;
  canvas.height = image.height;
  const ctx = canvas.getContext("2d");
  ctx.translate(image.width, 0);
  ctx.scale(-1, 1);
  ctx.drawImage(image, 0, 0);
  return canvas.toDataURL("image/png");
}

/** Default centred crop area (in source pixels) for a target aspect ratio. */
export async function centerCropArea(imageUrl, aspect) {
  const image = await loadImage(imageUrl);
  const imgAspect = image.width / image.height;
  let width = image.width;
  let height = image.height;
  if (imgAspect > aspect) {
    width = Math.round(image.height * aspect);
  } else {
    height = Math.round(image.width / aspect);
  }
  return {
    x: Math.round((image.width - width) / 2),
    y: Math.round((image.height - height) / 2),
    width,
    height,
  };
}

function rotateToCanvas(image, rotation) {
  const rad = (rotation * Math.PI) / 180;
  const sin = Math.abs(Math.sin(rad));
  const cos = Math.abs(Math.cos(rad));
  const width = image.width * cos + image.height * sin;
  const height = image.width * sin + image.height * cos;

  const canvas = document.createElement("canvas");
  canvas.width = Math.round(width);
  canvas.height = Math.round(height);
  const ctx = canvas.getContext("2d");
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.translate(canvas.width / 2, canvas.height / 2);
  ctx.rotate(rad);
  ctx.drawImage(image, -image.width / 2, -image.height / 2);
  return canvas;
}

function loadImage(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = url;
  });
}
